<template>
    <div class="pt-header-menu-container" flex="-">

        <a v-if="isMaterialPackage" class="pt-header-menu-item flex gap-2 items-center"  href="/material" target="_self">
            <MaterialIcon class="w-6 h-6" />
            <span class="font-semibold">{{ t('Product.MATERIAL') }}</span>
        </a>

        <template v-if="!control?.is_air_product">
            <PtHeaderDropdown trigger="hover" class="pt-header-menu-dropdown">
                <template #reference="{ open }">
                    <div class="pt-header-menu-item" :class="{ 'pt-header-menu-item-active': isDropdownActive }">
                        <span class="pt-header-menu-dropdown__reference">{{ t('Tools.Name') }}</span>
                        <PtIcon :icon="open ? 'SolidArrowUp' : 'SolidArrowDown'" />
                    </div>
                </template>

                <template #popper>
                    <ul class="p-2 bg-white text-black rounded">
                        <li class="flex gap-2 items-center p-1 text-black text-sm cursor-pointer mb-1 h-8 hover:bg-[#b3bac533] rounded flex-nowrap" v-for="tool in tools" :key="tool.label" @click="handleItemClick({name: tool.label, path: tool.path})">
                            <div class="whitespace-nowrap">{{ tool.label }}</div>
                            <div v-if="tool.is_beta" class="bg-eureka-500 rounded-sm px-1 text-[10px] leading-5 text-white italic">BETA</div>
                        </li>
                    </ul>
                </template>

            </PtHeaderDropdown>

            <div class="pt-header-menu-item" :class="{ 'pt-header-menu-item-active': EHeaderMenu.Monitor === activeMenu }"
                 @click="handleItemClick({
                     name: EHeaderMenu.Monitor,
                     path: '/monitor/#',
                 })">
                {{ t('Monitor') }}
            </div>

            <div class="pt-header-menu-item" :class="{ 'pt-header-menu-item-active': EHeaderMenu.Workspace === activeMenu }"
                 @click="handleItemClick({ name: EHeaderMenu.Workspace })">
                {{ t('Workspace') }}
            </div>
        </template>

        <TryMaterialButton v-if="showMaterialTrialButton"/>
    </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue';
import { PtHeaderDropdown, PtIcon } from 'patsnap-biz';
import { cn, tw, en, jp } from './locales';
import type { IAirControl } from '@eureka/utils/control';
import { controlDeferred, EurekaProduct } from '@eureka/utils/control';
import { EHeaderMenu, ERouteMenu, HOME_PAGE } from './const.ts';
import '@patsnap-ui/icon/assets/solid/ArrowUp.svg';
import '@patsnap-ui/icon/assets/solid/ArrowDown.svg';
import { Site, SLD } from '@patsnap-utils/biz';
import MaterialIcon from './MaterialIcon.vue';
import TryMaterialButton from './TryMaterialButton.vue';

import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import _ from 'lodash';

const route = useRoute();

const { t } = useI18n({
    messages: {
        cn, en, jp, tw,
    },
});

const toolsOptions = ref<{ name: EHeaderMenu; path: string, is_beta: boolean }[]>([]);

const control = ref<IAirControl>()

const isMaterialPackage = computed(() => {
    return control.value?.product === EurekaProduct.MATERIAL;
});

const showMaterialTrialButton = computed(() => {
    return (control.value?.freemium || control.value?.trial) && !isMaterialPackage.value && !control.value?.air_hightest;
});

const activeMenu = computed(() => {
    const name = _.get(window.location, 'pathname', '')?.replace(/\//g, '')?.toUpperCase();
    const routeName = _.toUpper(route.name as string)

    if (name === '404' || (!name && routeName !== HOME_PAGE)) return '';

    return ERouteMenu[name];
});

const isDropdownActive = computed(() => {
    return _.find(toolsOptions.value, { name: activeMenu.value });
});

const tools = computed(() => {
    return toolsOptions.value.map(item => {
        return {
            path: item.path,
            label: t(`Tools.${item.name}`),
            active: item.name === activeMenu.value,
            is_beta: item.is_beta,
        };
    })
});

const handleItemClick = (item: { name: EHeaderMenu, path?: string, active?: boolean }) => {
    if ((activeMenu.value && item.name === activeMenu.value) || item.active) return;
    if (item.name === EHeaderMenu.Workspace) {
        window.open(new Site({ sld: SLD.WORKSPACE }).href, '_blank');
        return;
    }
    item.path && window.open(item.path, '_self');
};

onMounted(async() => {
    control.value = await controlDeferred.promise;
    const { ai_disclosure, project, disclosure_check, is_air_product } = control.value;

    if (is_air_product) return;

    const toolsOptionsConfig = [
        { condition: isMaterialPackage.value, value: { name: EHeaderMenu.TechMind, path: '/whiteboard', is_beta: true } },
        { condition: disclosure_check, value: { name: EHeaderMenu.Idea, path: '/idea' } },
        { condition: ai_disclosure, value: { name: EHeaderMenu.AIDisclosure, path: '/ai-disclosure' } },
        { condition: project, value: { name: EHeaderMenu.Project, path: '/project' } },
        { condition: true, value: { name: EHeaderMenu.TechnologyRoadmap, path: '/technology-roadmap/#' } },
        { condition: isMaterialPackage.value, value: { name: EHeaderMenu.Workflow, path: '/material/workflow' } }
    ];

    toolsOptionsConfig.forEach(({ condition, value }) => {
        if (condition) {
            toolsOptions.value.push(value);
        }
    });
});
</script>

<style lang="scss">
.pt-header-menu-container {
    align-items: center;

    .pt-header-menu-dropdown {
        flex-shrink: 0;
        margin-right: 40px;

        .pt-header-menu-item {
            margin-right: 0
        }

        &__reference {
            margin-right: 2px;
        }

        &.patsnap-biz-header-dropdown.right .patsnap-biz-header-dropdown__popper {
            top: 48px;
            left: 0;
            right: auto;
        }

        .active.patsnap-ui-list-item .patsnap-ui-list-item__content {
            color: $brand-hover;
        }
    }

    .pt-header-menu-item {
        position: relative;
        color: $text-secondary;
        font-weight: 400;
        margin-right: 24px;
        cursor: pointer;
        height: 64px;
        line-height: 64px;
        flex-shrink: 0;
        min-width: 74px;
        text-align: center;

        &.pt-header-menu-dropdown {
            margin-right: 2px;
        }

        &:last-child {
            margin-right: 0;
        }

        &:hover {
            color: $brand-hover;

            >.patsnap-biz-icon {
                fill: #6DB9FF;
            }
        }

        &.pt-header-menu-item-active {
            color: $brand-hover;
            font-weight: 600;

            &::after {
                position: absolute;
                left: 0;
                bottom: 0;
                content: "";
                display: inline-block;
                height: 3px;
                width: 100%;
                border-radius: 3px;
                background: $brand-primary;
            }

            >.patsnap-biz-icon {
                fill: #6DB9FF;
            }
        }
    }
}
</style>
